/**
 * Persian uses the EXTENDED Arabic-Indic digits U+06F0–06F9 (۰۱۲۳۴۵۶۷۸۹), not
 * the Arabic block U+0660–0669 (٠١٢٣٤٥٦٧٨٩). They differ visibly at 4/5/6 and
 * carry different Unicode bidi classes, so the two are never interchangeable.
 *
 * A Persian keyboard types ۰۹۱۲… into a phone or link field, and a validator
 * written for [0-9] would silently reject it. So: normalise BOTH blocks to
 * ASCII before validation/storage, and localise only on display.
 */

const PERSIAN_ZERO = 0x06f0;
const ARABIC_ZERO = 0x0660;
const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";

export function normalizeDigits(input: string): string {
  let out = "";
  for (const ch of input) {
    const cp = ch.codePointAt(0)!;
    if (cp >= PERSIAN_ZERO && cp <= PERSIAN_ZERO + 9) out += String(cp - PERSIAN_ZERO);
    else if (cp >= ARABIC_ZERO && cp <= ARABIC_ZERO + 9) out += String(cp - ARABIC_ZERO);
    else out += ch;
  }
  return out;
}

/** ASCII digits → Persian digits, for display in the fa locale. */
export function toPersianDigits(input: string | number): string {
  return String(input).replace(/[0-9]/g, (d) => PERSIAN_DIGITS[Number(d)]!);
}

/** Locale-aware number formatting; `fa` yields Persian digits via the arabext numbering system. */
export function formatNumber(n: number, locale: "en" | "fa"): string {
  return new Intl.NumberFormat(locale === "fa" ? "fa-IR" : "en-GB").format(n);
}

/** mm:ss or h:mm:ss, digits localised. */
export function formatDuration(totalSeconds: number, locale: "en" | "fa"): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const core =
    h > 0
      ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
      : `${m}:${String(sec).padStart(2, "0")}`;
  return locale === "fa" ? toPersianDigits(core) : core;
}
